/* eslint-disable default-case, no-param-reassign, consistent-return */
/*
 *
 * Main reducer
 *
 */

import produce from 'immer';

import {
  ADD_DISMISSED_BANNER,
  ADD_DISMISSED_DOC_UPLOAD_BANNER,
  AUTH_INCOMPLETE,
  BIOMETRIC_LOGIN_REQUEST,
  BIOMETRIC_LOGIN_REQUEST_FAILURE,
  CANCEL_BIOMETRIC_AUTHENTICATION,
  CANCEL_CHANGE_SELECTED_LOAN_SUCCESS,
  CHANGE_SELECTED_LOAN,
  CHANGE_SELECTED_LOAN_FAILURE,
  CHANGE_SELECTED_LOAN_SUCCESS,
  CHATBOT_ERROR_BANNER_MESSAGE,
  CLOSE_MODAL,
  CONSENT_REQUEST,
  CONSENT_REQUEST_FAILURE,
  CONSENT_REQUEST_SUCCESS,
  DISABLE_INSTANT_POST_LOGIN_MODAL,
  DISABLE_MODAL,
  DISMISS_UPDATE_MODAL,
  DISPLAY_CHATBOT_ERROR_BANNER,
  FETCH_OIDC_CONFIG_FAILURE,
  FETCH_RESPA_LETTER,
  FETCH_RESPA_LETTER_FAILURE,
  FETCH_TRANSFER_STATUS,
  FETCH_TRANSFER_STATUS_FAILURE,
  FETCH_TRANSFER_STATUS_SUCCESS,
  INITIALIZE_OIDC_LOGIN,
  INITIALIZE_OIDC_LOGIN_CANCELLED,
  INITIALIZE_OIDC_LOGIN_FAILURE,
  INITIALIZE_REQUEST,
  INITIALIZE_REQUEST_FAILURE,
  INITIALIZE_REQUEST_SUCCESS,
  LINK_NATIVE_CLIENT,
  LINK_NATIVE_CLIENT_FAILURE,
  LINK_NATIVE_CLIENT_SUCCESS,
  LOGIN_REQUEST,
  LOGIN_REQUEST_CANCELLED,
  LOGIN_REQUEST_FAILURE,
  LOGIN_REQUEST_SUCCESS,
  LOGOUT_REQUEST,
  LOGOUT_REQUEST_SUCCESS,
  MFA_OPTION_REQUEST,
  MFA_OPTION_REQUEST_FAILURE,
  MFA_OPTION_REQUEST_SUCCESS,
  NATIVE_CLIENTS_REQUEST_FAILURE,
  OAUTH_LOGIN,
  OAUTH_LOGIN_FAILURE,
  OAUTH_LOGIN_SUCCESS,
  OPEN_MODAL,
  OPEN_PIN_DRAWER_FAILURE,
  PIN_LOGIN_REQUEST,
  PIN_LOGIN_REQUEST_FAILURE,
  PUT_LANGUAGE_PREFERENCE,
  PUT_LANGUAGE_PREFERENCE_FAILURE,
  PUT_LANGUAGE_PREFERENCE_SUCCESS,
  PUT_PAPERLESS_SETTING,
  PUT_PAPERLESS_SETTING_FAILURE,
  PUT_PAPERLESS_SETTING_SUCCESS,
  REFRESH_TOKEN_FAILURE,
  RESET_MAIN_ERROR,
  RESET_MAIN_SUCCESS,
  RESET_SSO_MIGRATION,
  RESET_STATUS,
  RESET_USER_STATE,
  SET_CHATBOT_AUTH_TOKEN,
  SET_CHATBOT_WINDOW_STATUS,
  SET_INSTANT_POST_LOGIN_MODAL_STEP,
  SET_IS_CHANGE_SELECTED_LOAN_IN_PROGRESS,
  SET_IS_SSL_AUTH,
  SET_RESPA_FILE,
  SET_SELECTED_LANGUAGE,
  SET_SESSION_STATE,
  SET_SHOULD_RENDER_SPLASH_SCREEN,
  SET_SSO_MIGRATION,
  TOGGLE_RESPA_DRAWER,
  TOTP_REQUEST,
  TOTP_REQUEST_FAILURE,
  TOTP_REQUEST_SUCCESS,
  UPDATE_DISMISSED_BANNERS,
  UPDATE_DISMISSED_DOC_UPLOAD_BANNERS,
  UPDATE_IS_LOGGED_IN,
  UPDATE_LOANS,
  UPDATE_PRIMARY_LOAN,
  UPDATE_REFRESH_TOKEN,
  UPDATE_SHOULD_BIOMETRICS_OPEN_AUTOMATICALLY,
  UPDATE_SSO_MIGRATION_URL,
  UPDATE_USER_LOANS_DATA,
  UPDATE_USER_PROFILE,
} from './constants';
import { CHANGE_INPUT as LOGIN_CHANGE_INPUT } from '../Login/constants';
import { LANGUAGE_ENGLISH } from '../../utils/constants';
import Platform from '../../utils/Platform';

const isNativeMobile = Platform.OS === 'ios' || Platform.OS === 'android';

export const initialState = {
  chatbotAuthToken: '',
  chatbotWindowStatus: 'closed',
  clientInfo: {
    clientAuthentication: {},
    clientFeaturesNative: { hideLogoutButton: false },
    clientFeaturesWeb: { hideLogoutButton: false },
    clientName: '',
    colors: {},
    contactInfoPageCollectionsDepartmentPhoneNumber: '',
    customerServicePhoneNumber: '',
    customHeaderLogoHeightInPx: null,
    disasterAssistanceDisclosurePhoneNumber: '',
    fonts: {},
    footerIconKeys: [],
    footerLogo: { image: null },
    hardshipContactCardPhoneNumber: '',
    hasNativeSso: false,
    logo: { image: null, type: null },
    storeVersion: '',
    version: '',
  },
  dismissedBanners: [],
  dismissedDocUploadStatusBanners: [],
  error: {
    chatbot: false,
    main: false,
    pin: false,
    respaLetter: false,
    serviceError: false,
    transferStatus: false,
  },
  file: {},
  instantPostLoginModal: {
    isEnabled: true,
    languagePreference: {
      allowedLanguages: [],
      allowedOperation: '',
    },
    paperlessStatus: { paperlessBilling: '' },
    step: 1,
  },
  internationalization: { language: LANGUAGE_ENGLISH },
  isAccountModalOpen: false,
  isChangeSelectedLoanInProgress: false,
  isDrawerOpen: false,
  isInactivityModalOpen: false,
  isInitComplete: false,
  isLoggedIn: false,
  isLoggedInViaInitRequest: false,
  isLoggedOutViaSsoGeneric: false,
  isSslAuth: false,
  isSwitchLoanModalOpen: false,
  isUpdateModalDismissed: false,
  loading: false,
  loanDetails: {},
  loans: [],
  profile: {},
  refreshTokenUpdated: false,
  sameClientCodes: false,
  selectedLoan: '',
  sessionState: AUTH_INCOMPLETE,
  shouldBiometricsOpenAutomatically: true,
  shouldRenderSplashScreen: isNativeMobile,
  ssoLogoutUrl: '',
  ssoMigrationUrl: '',
  status: {
    consent: 'idle',
    fetchTransferStatus: 'idle',
    initializeRequestStatus: 'idle',
    login: 'idle',
    mfa: 'idle',
    putLanguagePreference: 'idle',
    putPaperlessSetting: 'idle',
    totp: 'idle',
    welcome: 'idle',
  },
  success: false,
  targetedUser: { ssoMigration: {} },
  testFlags: { showLanguagePicker: false },
  transferStatus: {},
  treatments: {},
  userEmail: '',
};

const mainReducer = produce((draft, { payload, type }) => {
  switch (type) {
    case ADD_DISMISSED_BANNER: {
      const { bannerName } = payload;
      draft.dismissedBanners.push(bannerName);
      break;
    }
    case ADD_DISMISSED_DOC_UPLOAD_BANNER: {
      const { documentId } = payload;
      draft.dismissedDocUploadStatusBanners.push(documentId);
      break;
    }
    case BIOMETRIC_LOGIN_REQUEST: {
      const { shouldResetBanners = true } = payload;
      if (shouldResetBanners) {
        draft.error.main = false;
        draft.success = '';
      }
      break;
    }
    case BIOMETRIC_LOGIN_REQUEST_FAILURE: {
      const { error } = payload;
      draft.error.main = error;
      draft.success = '';
      break;
    }
    case CANCEL_BIOMETRIC_AUTHENTICATION: {
      draft.loading = false;
      break;
    }
    case CANCEL_CHANGE_SELECTED_LOAN_SUCCESS: {
      draft.loading = false;
      break;
    }
    case CHANGE_SELECTED_LOAN: {
      draft.error.main = false;
      draft.loading = true;
      break;
    }
    case CHANGE_SELECTED_LOAN_FAILURE: {
      const { error } = payload;
      draft.error.main = error;
      draft.loading = false;
      break;
    }
    case CHANGE_SELECTED_LOAN_SUCCESS: {
      const {
        clientInfo,
        loanDetails,
        selectedLoan,
        selectedLoanLanguagePreference,
        treatments,
      } = payload;
      draft.clientInfo = { ...initialState.clientInfo, ...clientInfo };
      draft.instantPostLoginModal.languagePreference = selectedLoanLanguagePreference;
      draft.loading = false;
      draft.loanDetails = loanDetails;
      draft.selectedLoan = selectedLoan;
      draft.treatments = treatments;
      if (selectedLoanLanguagePreference) {
        draft.instantPostLoginModal.languagePreference = selectedLoanLanguagePreference;
        draft.instantPostLoginModal.isEnabled = true;
      } else {
        draft.instantPostLoginModal.languagePreference =
          initialState.instantPostLoginModal.languagePreference;
      }
      break;
    }
    case CLOSE_MODAL: {
      const { modalState } = payload;
      draft[modalState] = false;
      break;
    }
    case CONSENT_REQUEST: {
      draft.status.consent = 'loading';
      draft.error.main = false;
      break;
    }
    case CONSENT_REQUEST_FAILURE: {
      const { error } = payload;
      draft.status.consent = 'failed';
      draft.error.main = error;
      break;
    }
    case CONSENT_REQUEST_SUCCESS: {
      draft.status.consent = 'succeeded';
      break;
    }
    case DISABLE_INSTANT_POST_LOGIN_MODAL: {
      draft.instantPostLoginModal.isEnabled = false;
      break;
    }
    case DISABLE_MODAL: {
      const { modalState } = payload;
      draft[modalState] = true;
      break;
    }
    case DISMISS_UPDATE_MODAL: {
      draft.isUpdateModalDismissed = true;
      break;
    }
    case DISPLAY_CHATBOT_ERROR_BANNER: {
      draft.error.chatbot = CHATBOT_ERROR_BANNER_MESSAGE;
      break;
    }
    case FETCH_OIDC_CONFIG_FAILURE: {
      const { error } = payload;
      draft.error.main = error;
      break;
    }
    case FETCH_RESPA_LETTER: {
      draft.error.respaLetter = false;
      break;
    }
    case FETCH_RESPA_LETTER_FAILURE: {
      const { error } = payload;
      draft.error.respaLetter = error;
      break;
    }
    case FETCH_TRANSFER_STATUS: {
      draft.error.transferStatus = false;
      draft.status.fetchTransferStatus = 'loading';
      draft.transferStatus = initialState.transferStatus;
      break;
    }
    case FETCH_TRANSFER_STATUS_FAILURE: {
      const { error } = payload;
      draft.error.transferStatus = error;
      draft.status.fetchTransferStatus = 'failed';
      draft.transferStatus = initialState.transferStatus;
      break;
    }
    case FETCH_TRANSFER_STATUS_SUCCESS: {
      const { servicingTransferDetail } = payload;
      draft.status.fetchTransferStatus = 'succeeded';
      draft.transferStatus = servicingTransferDetail;
      break;
    }
    case INITIALIZE_OIDC_LOGIN: {
      draft.error.main = false;
      draft.success = false;
      draft.loading = true;
      break;
    }
    case INITIALIZE_OIDC_LOGIN_CANCELLED: {
      draft.loading = false;
      break;
    }
    case INITIALIZE_OIDC_LOGIN_FAILURE: {
      const { error } = payload;
      draft.error.main = error;
      draft.loading = false;
      break;
    }
    case INITIALIZE_REQUEST: {
      draft.error.main = false;
      draft.loading = true;
      break;
    }
    case INITIALIZE_REQUEST_FAILURE: {
      const { error } = payload;
      draft.error.main = error;
      draft.isInitComplete = true;
      draft.loading = false;
      draft.status.initializeRequestStatus = 'failed';
      break;
    }
    case INITIALIZE_REQUEST_SUCCESS: {
      const { isLoggedIn = false } = payload;
      draft.isLoggedIn = isLoggedIn;
      if (isLoggedIn) {
        draft.isLoggedInViaInitRequest = true;
      }
      draft.isInitComplete = true;
      draft.loading = false;
      break;
    }
    case LINK_NATIVE_CLIENT: {
      draft.error.main = false;
      draft.status.welcome = 'loading';
      break;
    }
    case LINK_NATIVE_CLIENT_FAILURE: {
      const { bannerError, serviceError } = payload;
      if (bannerError) {
        draft.error.main = bannerError;
      } else if (serviceError) {
        draft.error.serviceError = serviceError;
      }
      draft.status.welcome = 'failed';
      break;
    }
    case LINK_NATIVE_CLIENT_SUCCESS: {
      draft.status.welcome = 'succeeded';
      break;
    }
    case LOGIN_CHANGE_INPUT: {
      draft.error.main = false;
      draft.success = '';
      break;
    }
    case LOGIN_REQUEST: {
      draft.error.main = false;
      draft.status.login = 'loading';
      draft.success = '';
      break;
    }
    case LOGIN_REQUEST_CANCELLED: {
      draft.status.login = 'idle';
      break;
    }
    case LOGIN_REQUEST_FAILURE: {
      draft.error.main = payload;
      draft.status.login = 'failed';
      break;
    }
    case LOGIN_REQUEST_SUCCESS: {
      draft.status.login = 'succeeded';
      break;
    }
    case LOGOUT_REQUEST: {
      draft.error.main = false;
      draft.loading = true;
      break;
    }
    case LOGOUT_REQUEST_SUCCESS: {
      const { message, shouldBiometricsOpenAutomatically } = payload;
      draft.shouldBiometricsOpenAutomatically = shouldBiometricsOpenAutomatically;
      draft.success = message;
      break;
    }
    case MFA_OPTION_REQUEST: {
      draft.status.mfa = 'loading';
      draft.error.main = false;
      break;
    }
    case MFA_OPTION_REQUEST_FAILURE: {
      const { error } = payload;
      draft.status.mfa = 'failed';
      draft.error.main = error;
      break;
    }
    case MFA_OPTION_REQUEST_SUCCESS: {
      draft.status.mfa = 'succeeded';
      break;
    }
    case NATIVE_CLIENTS_REQUEST_FAILURE: {
      const { error } = payload;
      draft.error.serviceError = error;
      draft.loading = false;
      draft.isInitComplete = true;
      break;
    }
    case OAUTH_LOGIN: {
      draft.error.main = false;
      draft.loading = true;
      break;
    }
    case OAUTH_LOGIN_FAILURE: {
      const { error } = payload;
      draft.error.main = error;
      draft.loading = false;
      break;
    }
    case OAUTH_LOGIN_SUCCESS: {
      draft.loading = false;
      break;
    }
    case OPEN_MODAL: {
      const { modalState } = payload;
      draft[modalState] = true;
      break;
    }
    case OPEN_PIN_DRAWER_FAILURE: {
      const { error } = payload;
      draft.error.main = error;
      break;
    }
    case PIN_LOGIN_REQUEST: {
      draft.error.pin = false;
      draft.loading = true;
      break;
    }
    case PIN_LOGIN_REQUEST_FAILURE: {
      const { error, isDrawerError } = payload;
      if (isDrawerError) {
        draft.error.pin = error;
      } else {
        draft.error.main = error;
      }
      draft.loading = false;
      break;
    }
    case PUT_LANGUAGE_PREFERENCE: {
      draft.status.putLanguagePreference = 'loading';
      break;
    }
    case PUT_LANGUAGE_PREFERENCE_FAILURE: {
      draft.status.putLanguagePreference = 'failed';
      break;
    }
    case PUT_LANGUAGE_PREFERENCE_SUCCESS: {
      draft.status.putLanguagePreference = 'succeeded';
      break;
    }
    case PUT_PAPERLESS_SETTING: {
      draft.status.putPaperlessSetting = 'loading';
      break;
    }
    case PUT_PAPERLESS_SETTING_FAILURE: {
      draft.status.putPaperlessSetting = 'failed';
      break;
    }
    case PUT_PAPERLESS_SETTING_SUCCESS: {
      draft.status.putPaperlessSetting = 'succeeded';
      break;
    }
    case REFRESH_TOKEN_FAILURE: {
      draft.isLoggedIn = false;
      draft.refreshTokenUpdated = false;
      break;
    }
    case RESET_MAIN_ERROR: {
      const { prop } = payload;
      if (prop) {
        draft.error[prop] = false;
      } else {
        draft.error = initialState.error;
      }
      break;
    }
    case RESET_MAIN_SUCCESS: {
      draft.success = '';
      break;
    }
    case RESET_SSO_MIGRATION: {
      draft.targetedUser.ssoMigration = initialState.targetedUser.ssoMigration;
      break;
    }
    case RESET_STATUS: {
      const { view } = payload;
      draft.status[view] = initialState.status[view];
      break;
    }
    case RESET_USER_STATE: {
      const { colors = {}, fonts = {} } = draft.clientInfo || {};
      return {
        ...initialState,
        clientInfo: {
          ...initialState.clientInfo,
          colors,
          fonts,
        },
        isUpdateModalDismissed: true,
        shouldBiometricsOpenAutomatically: false,
        shouldRenderSplashScreen: false,
        success: draft.success,
      };
    }
    case SET_CHATBOT_AUTH_TOKEN: {
      const { chatbotAuthToken } = payload;
      draft.chatbotAuthToken = chatbotAuthToken;
      break;
    }
    case SET_CHATBOT_WINDOW_STATUS: {
      const { status } = payload;
      draft.chatbotWindowStatus = status;
      break;
    }
    case SET_IS_CHANGE_SELECTED_LOAN_IN_PROGRESS: {
      const { value } = payload;
      draft.isChangeSelectedLoanInProgress = value;
      break;
    }
    case SET_INSTANT_POST_LOGIN_MODAL_STEP: {
      const { step } = payload;
      draft.instantPostLoginModal.step = step;
      break;
    }
    case SET_IS_SSL_AUTH: {
      const { isSslAuth } = payload;
      draft.isSslAuth = isSslAuth;
      break;
    }
    case SET_RESPA_FILE: {
      const { fileName, uri } = payload || {};
      if (fileName && uri) {
        draft.file = { fileName, uri };
      } else {
        draft.file = {};
      }
      break;
    }
    case SET_SELECTED_LANGUAGE: {
      const { language } = payload;
      draft.internationalization.language = language;
      break;
    }
    case SET_SESSION_STATE: {
      const { sessionState } = payload;
      draft.sessionState = sessionState;
      break;
    }
    case SET_SHOULD_RENDER_SPLASH_SCREEN: {
      const { value } = payload;
      draft.shouldRenderSplashScreen = value;
      break;
    }
    case SET_SSO_MIGRATION: {
      const {
        currentUserEmail,
        migrationImage,
        migrationLoginPage,
        migrationUrl,
      } = payload;
      draft.targetedUser.ssoMigration = {
        currentUserEmail,
        migrationImage,
        migrationLoginPage,
        migrationUrl,
      };
      break;
    }
    case TOGGLE_RESPA_DRAWER: {
      const { drawerState } = payload;
      draft.isDrawerOpen = drawerState;
      break;
    }
    case TOTP_REQUEST: {
      draft.status.totp = 'loading';
      draft.error.main = false;
      break;
    }
    case TOTP_REQUEST_FAILURE: {
      const { error } = payload;
      draft.status.totp = 'failed';
      draft.error.main = error;
      break;
    }
    case TOTP_REQUEST_SUCCESS: {
      draft.status.totp = 'succeeded';
      break;
    }
    case UPDATE_DISMISSED_BANNERS: {
      const { dismissedBanners } = payload;
      draft.dismissedBanners = dismissedBanners;
      break;
    }
    case UPDATE_DISMISSED_DOC_UPLOAD_BANNERS: {
      const { dismissedDocUploadStatusBanners } = payload;
      draft.dismissedDocUploadStatusBanners = dismissedDocUploadStatusBanners;
      break;
    }
    case UPDATE_IS_LOGGED_IN: {
      const { isLoggedIn, isLoggedOutViaSsoGeneric } = payload;
      draft.isLoggedIn = isLoggedIn;
      draft.isLoggedOutViaSsoGeneric = isLoggedOutViaSsoGeneric;
      break;
    }
    case UPDATE_LOANS: {
      const { loans } = payload;
      draft.loans = loans;
      break;
    }
    case UPDATE_PRIMARY_LOAN: {
      const { loanNumber } = payload;
      // eslint-disable-next-line array-callback-return
      draft.loans.map(({ hashedLoanNumber }, index) => {
        if (hashedLoanNumber === loanNumber) {
          draft.loans[index].isPrimaryLoan = true;
        } else {
          draft.loans[index].isPrimaryLoan = false;
        }
      });
      break;
    }
    case UPDATE_REFRESH_TOKEN: {
      const { value } = payload;
      draft.refreshTokenUpdated = value;
      break;
    }
    case UPDATE_SHOULD_BIOMETRICS_OPEN_AUTOMATICALLY: {
      const { shouldBiometricsOpenAutomatically } = payload;
      draft.shouldBiometricsOpenAutomatically = shouldBiometricsOpenAutomatically;
      break;
    }
    case UPDATE_SSO_MIGRATION_URL: {
      const { migrationUrl } = payload;
      draft.ssoMigrationUrl = migrationUrl;
      break;
    }
    case UPDATE_USER_LOANS_DATA: {
      const {
        clientInfo,
        loanDetails = initialState.loanDetails,
        loans,
        primaryLoanPaperlessStatus = initialState.instantPostLoginModal.paperlessStatus,
        profile,
        sameClientCodes,
        selectedLoan = initialState.selectedLoan,
        selectedLoanLanguagePreference = initialState.instantPostLoginModal.languagePreference,
        ssoLogoutUrl,
        testFlags,
        treatments,
      } = payload;
      const { paperlessBilling } = primaryLoanPaperlessStatus;
      draft.clientInfo = { ...draft.clientInfo, ...clientInfo };
      draft.instantPostLoginModal.paperlessStatus.paperlessBilling = paperlessBilling;
      draft.instantPostLoginModal.languagePreference = selectedLoanLanguagePreference;
      draft.loanDetails = loanDetails;
      draft.loans = loans;
      draft.profile = profile;
      draft.sameClientCodes = sameClientCodes;
      draft.selectedLoan = selectedLoan;
      draft.ssoLogoutUrl = ssoLogoutUrl;
      draft.testFlags = { ...draft.testFlags, ...testFlags };
      draft.treatments = treatments || draft.treatments;
      break;
    }
    case UPDATE_USER_PROFILE: {
      const { currentSetting } = payload;
      draft.profile.currentSetting = currentSetting;
      break;
    }
  }
}, initialState);

export default mainReducer;
